import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../../components/Seo';
import FaqSection from '../../components/Faq';
import { useTheme } from '../../theme';
import { articleSchema, faqPageSchema, breadcrumbSchema, Faq } from '../../lib/schema';

const PATH = '/base64-database-storage';

const FAQS: Faq[] = [
  { q: 'Should I store files as Base64 text in a database?', a: 'For small blobs (a few KB, like a config value or small thumbnail) it can be simpler to store Base64 text in a regular column. For larger files, a dedicated BLOB/bytea column, or better, object storage (like S3) with just a reference URL in the database, scales much better.' },
  { q: 'Does Base64 encoding make database storage bigger?', a: 'Yes — about 33% bigger than storing the same data as raw bytes, plus your database\'s usual per-row and index overhead on top of that.' },
  { q: 'Why do Kubernetes Secrets store values as Base64?', a: 'Kubernetes Secret manifests are YAML/JSON, which is text-only, so binary or multi-line values are Base64 encoded to fit safely into a text field — this is encoding for compatibility, not encryption for security.' },
  { q: 'Is Base64 storage secure by itself?', a: 'No — it is not encryption. If the data is sensitive, you still need encryption at rest and proper access controls regardless of whether it is stored as Base64 text or raw bytes.' },
];

export default function Base64DatabaseStoragePage() {
  const { themeClasses } = useTheme();
  const heading = `text-2xl font-bold ${themeClasses.textPrimary} mt-10 mb-4`;
  const para = `${themeClasses.textSecondary} leading-relaxed mb-4`;

  return (
    <>
      <Seo
        title="When (and When Not) to Store Files as Base64 in a Database | EasyBase64"
        description="Should you store binary data as Base64 text in a database column? Compare Base64 columns, BLOB columns and object storage, with real tradeoffs."
        path={PATH}
        jsonLd={[
          articleSchema({
            headline: 'When (and When Not) to Store Files as Base64 in a Database',
            description: 'Tradeoffs between storing binary data as Base64 text, in a BLOB column, or in object storage.',
            path: PATH,
            datePublished: '2026-07-07',
            dateModified: '2026-07-07',
          }),
          faqPageSchema(FAQS),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Base64 Database Storage', path: PATH }]),
        ]}
      />

      <div className="max-w-3xl mx-auto">
        <article className={`${themeClasses.infoCard} rounded-2xl p-6 sm:p-8 md:p-12 border mb-12`}>
          <h1 className={`text-4xl font-bold tracking-tight ${themeClasses.textPrimary} mb-6`}>
            When (and When Not) to Store Files as Base64 in a Database
          </h1>

          <p className={para}>
            It's tempting to Base64 encode a file and drop it straight into a text column — no new
            column type, no separate storage system, just a string. Sometimes that's fine. Often
            it isn't. Here's how to decide.
          </p>

          <h2 className={heading}>When a Base64 text column makes sense</h2>
          <p className={para}>
            For small values — a config blob, a tiny thumbnail, a serialized key, a Kubernetes-style
            Secret value — storing Base64 text in a normal column is simple and avoids introducing a
            binary column type or a separate storage service. It also travels cleanly through
            JSON-based APIs, ORMs, and text-based exports without any special handling.
          </p>

          <h2 className={heading}>The costs that show up at scale</h2>
          <p className={para}>
            Base64 adds about a{' '}
            <Link to="/base64-file-size-overhead" className="underline">33% size overhead</Link>{' '}
            versus storing raw bytes, which compounds with your database's own per-row and index
            overhead. Text columns storing large Base64 blobs also bloat query results, backups, and
            replication traffic — a <code>SELECT *</code> that incidentally includes a large Base64
            column moves far more data than one that includes a lightweight reference.
          </p>

          <h2 className={heading}>The alternatives</h2>
          <p className={para}>
            A dedicated binary column (<code>BLOB</code> in MySQL, <code>bytea</code> in
            PostgreSQL) stores raw bytes directly, avoiding the Base64 overhead entirely while
            staying inside your database. For larger files — images, videos, documents — object
            storage (S3, GCS, Azure Blob Storage, etc.) with just a URL or object key stored in the
            database scales much better: it separates file storage from your transactional
            database, enables CDN caching, and keeps your database backups and queries fast.
          </p>

          <h2 className={heading}>A common real-world case: Kubernetes Secrets</h2>
          <p className={para}>
            Kubernetes Secret manifests are YAML/JSON — text formats — so binary or multi-line
            values are Base64 encoded to fit safely as a text field value (see{' '}
            <Link to="/yaml-to-base64" className="underline">YAML to Base64</Link>). This is purely
            for text-safety, not security: Kubernetes Secrets still require encryption at rest and
            RBAC to actually protect the data, since Base64 alone{' '}
            <Link to="/is-base64-encryption" className="underline">provides no confidentiality</Link>.
          </p>

          <h2 className={heading}>Rule of thumb</h2>
          <p className={para}>
            Small, infrequently-large, text-adjacent values (config, small keys, Secret fields): a
            Base64 text column is fine. Larger or frequently-accessed binary files (images, videos,
            documents, backups): use a binary column or, better, object storage with a reference in
            the database.
          </p>
        </article>

        <FaqSection title="Base64 Database Storage — FAQ" faqs={FAQS} />
      </div>
    </>
  );
}
